import React, { useEffect, useMemo, useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { getAdherentsByGroup } from "../../../services/dashboardServices";
import { getDojos } from "../../../services/dojoServices";
import { getGroupsList } from "../../../services/groupServices";

/* Util: normaliser les noms (pour merger counts <-> groups) */
function normalizeName(s) {
  return String(s || "")
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();
}

/* Tooltip custom */
function ChartTooltip({ active, payload, label }) {
  if (!active || !payload || !payload.length) return null;
  const row = payload[0]?.payload || {};
  const used = payload.find((p) => p.dataKey === "adherentCount")?.value ?? 0;
  const rest = payload.find((p) => p.dataKey === "remainingQuota")?.value ?? 0;
  const total = used + rest;
  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow p-3 text-sm">
      <div className="font-semibold text-slate-900 dark:text-white mb-1">{label}</div>
      {row.dojoNom && (
        <div className="text-xs text-slate-600 dark:text-slate-300 mb-1">
          Dojo : <span className="font-medium">{row.dojoNom}</span>
        </div>
      )}
      <div className="text-slate-700 dark:text-slate-300">
        <div>Inscrits : <span className="font-medium">{used}</span></div>
        <div>Quota restant : <span className="font-medium">{rest}</span></div>
        <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">
          Capacité totale : {total} — Remplissage : {row.fillRate ?? 0}%
        </div>
      </div>
    </div>
  );
}

/* Skeleton */
function Skeleton() {
  return (
    <div className="rounded-2xl ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/90 dark:bg-slate-900/60 backdrop-blur p-4 sm:p-5">
      <div className="h-4 w-64 bg-slate-200 dark:bg-slate-800 animate-pulse rounded mb-4" />
      <div className="h-[260px] rounded-xl bg-slate-100 dark:bg-slate-800 animate-pulse" />
      <div className="mt-3 h-3 w-32 bg-slate-200 dark:bg-slate-800 animate-pulse rounded" />
    </div>
  );
}

export default function GroupDistributionChart() {
  const [rawCounts, setRawCounts] = useState([]); // {nom, adherentCount, quota_groupe, ordre_groupe}
  const [dojos, setDojos] = useState([]);         // {id, dojo_nom, ...}
  const [groups, setGroups] = useState([]);       // {id, nom, dojo_id, is_active, quota_groupe, ordre_groupe}
  const [selectedDojo, setSelectedDojo] = useState(""); // "" = Tous
  const [loading, setLoading] = useState(true);

  const isDark =
    document.documentElement.classList.contains("dark") ||
    document.body.classList.contains("dark") ||
    document.body.classList.contains("dark-mode");

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const [dojoList, counts, groupsList] = await Promise.all([
          getDojos(),
          getAdherentsByGroup(),
          getGroupsList(),
        ]);

        setDojos(Array.isArray(dojoList) ? dojoList : []);
        setRawCounts(Array.isArray(counts) ? counts : []);
        setGroups(Array.isArray(groupsList) ? groupsList : []);

        // Debug (si besoin)
        // console.log("[Dojos]", dojoList);
        // console.log("[Counts]", counts);
        // console.log("[Groups]", groupsList);
      } catch (e) {
        console.error("Erreur chargement dojos/comptages/groupes :", e);
        setDojos([]);
        setRawCounts([]);
        setGroups([]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  /* Index Dojo par id */
  const dojoById = useMemo(() => {
    const map = new Map();
    (dojos || []).forEach((d) => map.set(String(d.id), d));
    return map;
  }, [dojos]);

  /* Index counts par nom normalisé */
  const countsByName = useMemo(() => {
    const map = new Map();
    (rawCounts || []).forEach((c) => {
      const key = normalizeName(c.nom);
      if (!key) return;
      map.set(key, {
        adherentCount: Number(c.adherentCount || 0),
        quota_groupe: Number(c.quota_groupe || 0),
        ordre_groupe: Number(c.ordre_groupe || 9999),
        nom: c.nom,
      });
    });
    return map;
  }, [rawCounts]);

  /* Fusion Groups (actifs) + Counts (par nom) + Dojo */
  const merged = useMemo(() => {
    const rows = (groups || [])
      .filter((g) => !!g.is_active)
      .map((g) => {
        const key = normalizeName(g.nom);
        const c = countsByName.get(key);

        const adherentCount = Number(c?.adherentCount || 0);
        const quota = Math.max(
          0,
          Number(g?.quota_groupe ?? c?.quota_groupe ?? 0)
        );
        const remainingQuota = Math.max(0, quota - adherentCount);
        const ordre = Number(
          g?.ordre_groupe ?? c?.ordre_groupe ?? 9999
        );
        const dojoId = String(g?.dojo_id ?? "");
        const dojoNom = dojoById.get(dojoId)?.dojo_nom || null;
        const fillRate =
          quota > 0 ? Math.round((adherentCount / quota) * 100) : 0;

        return {
          id: Number(g.id),
          nom: g.nom || c?.nom || "Sans nom",
          adherentCount,
          remainingQuota,
          quota,
          ordre,
          dojoId,
          dojoNom,
          fillRate,
        };
      });

    // Tri : Dojo → ordre_groupe → nom
    return rows.sort((a, b) => {
      if ((a.dojoNom || "") !== (b.dojoNom || "")) {
        return String(a.dojoNom || "").localeCompare(String(b.dojoNom || ""), "fr");
      }
      if (a.ordre !== b.ordre) return a.ordre - b.ordre;
      return String(a.nom).localeCompare(String(b.nom), "fr");
    });
  }, [groups, countsByName, dojoById]);

  /* Filtre par dojo */
  const filtered = useMemo(() => {
    if (!selectedDojo) return merged;
    return merged.filter((x) => String(x.dojoId) === String(selectedDojo));
  }, [merged, selectedDojo]);

  const selectedDojoName =
    dojoById.get(String(selectedDojo))?.dojo_nom || "";

  const chartHeight = Math.max(260, 40 * filtered.length + 80);

  /* Label % à droite de la barre “Inscrits” */
  const PercentLabel = ({ x, y, width, height, payload }) => {
    if (!payload) return null;
    const pct = payload.fillRate ?? 0;
    const text = `${pct}%`;
    const tx = (x || 0) + (width || 0) + 6;
    const ty = (y || 0) + (height || 0) / 2 + 4;
    return (
      <text
        x={tx}
        y={ty}
        fontSize={12}
        fill={isDark ? "#cbd5e1" : "#334155"}
      >
        {text}
      </text>
    );
  };

  if (loading) return <Skeleton />;

  if (!filtered.length) {
    return (
      <section className="rounded-2xl overflow-hidden ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/90 dark:bg-slate-900/60 backdrop-blur p-4 sm:p-5">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="text-base sm:text-lg font-semibold text-slate-900 dark:text-slate-100">
              Répartition des adhérents par groupes actifs
            </h3>
            <div className="text-[11px] text-slate-500 dark:text-slate-400">
              0 groupe(s){selectedDojo ? ` — ${selectedDojoName}` : ""}
            </div>
          </div>
        </div>
        <div className="text-center py-12 text-slate-600 dark:text-slate-400">
          Aucun groupe actif à afficher{selectedDojo ? " pour ce dojo." : "."}
        </div>
      </section>
    );
  }

  return (
    <section className="rounded-2xl overflow-hidden ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/90 dark:bg-slate-900/60 backdrop-blur">
      {/* En-tête + filtre dojo */}
      <div className="px-5 pt-5 sm:px-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h3 className="text-base sm:text-lg font-semibold text-slate-900 dark:text-slate-100">
              Répartition des adhérents par groupes actifs
            </h3>
            <div className="text-[11px] text-slate-500 dark:text-slate-400">
              {filtered.length} groupe{filtered.length > 1 ? "s" : ""}
              {selectedDojo && <> · Dojo&nbsp;<span className="font-medium">{selectedDojoName}</span></>}
            </div>
          </div>

          {/* Pills dojo (scrollable) */}
          <div className="flex items-center gap-2 overflow-x-auto max-w-full py-1">
            <button
              type="button"
              onClick={() => setSelectedDojo("")}
              className={[
                "whitespace-nowrap rounded-xl px-3 py-1.5 text-sm ring-1 transition",
                !selectedDojo
                  ? "bg-violet-600 text-white ring-transparent"
                  : "bg-white/70 dark:bg-slate-800/70 text-slate-800 dark:text-slate-200 ring-slate-300 dark:ring-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800",
              ].join(" ")}
              title="Tous les dojos"
            >
              Tous
            </button>
            {dojos.map((d) => (
              <button
                key={d.id}
                type="button"
                onClick={() => setSelectedDojo(String(d.id))}
                className={[
                  "whitespace-nowrap rounded-xl px-3 py-1.5 text-sm ring-1 transition",
                  String(selectedDojo) === String(d.id)
                    ? "bg-blue-600 text-white ring-transparent"
                    : "bg-white/70 dark:bg-slate-800/70 text-slate-800 dark:text-slate-200 ring-slate-300 dark:ring-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800",
                ].join(" ")}
                title={d.dojo_nom || `Dojo ${d.id}`}
              >
                {d.dojo_nom || `Dojo ${d.id}`}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Graphique */}
      <div className="px-5 pb-5 sm:px-6">
        <div className="w-full" style={{ height: chartHeight }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              layout="vertical"
              data={filtered}
              margin={{ top: 12, right: 48, left: 8, bottom: 8 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke={isDark ? "#334155" : "#e5e7eb"} />
              <XAxis
                type="number"
                axisLine={false}
                tickLine={false}
                tick={{ fill: isDark ? "#cbd5e1" : "#334155" }}
              />
              <YAxis
                type="category"
                dataKey="nom"
                width={220}
                axisLine={false}
                tickLine={false}
                tick={{ fill: isDark ? "#cbd5e1" : "#334155" }}
              />
              <Tooltip content={<ChartTooltip />} />
              <Legend wrapperStyle={{ color: isDark ? "#cbd5e1" : "#334155", fontSize: 12 }} />
              <Bar
                dataKey="adherentCount"
                stackId="a"
                name="Inscrits"
                radius={[4, 4, 0, 0]}
                fill="#3B82F6" // blue-500
                label={<PercentLabel />}
              />
              <Bar
                dataKey="remainingQuota"
                stackId="a"
                name="Quota restant"
                radius={[0, 0, 4, 4]}
                fill="#34D399" // emerald-500
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </section>
  );
}
